import { VoxelAsset, VoxelData } from '../../voxelTypes';
import { PALETTE } from '../../voxelColor';
import { getColors, addBox, addMiniBox, ExteriorFinish } from './CatalogUtils';

export function getExteriorCatalog(finish: ExteriorFinish): VoxelAsset[] {
  const assets: VoxelAsset[] = [];
  const c = getColors({ exteriorFinish: finish });

  // 1. Acorn Mailbox
  (() => {
    const voxels: VoxelData[] = [];
    // Wood post
    addBox(voxels, -1, 0, -1, 2, 5, 2, c.woodPrimary);
    // Grass/moss details at base
    addBox(voxels, -1.5, 0, -1.5, 3, 0.8, 3, PALETTE.MOSS);
    // Acorn caps (dark brown)
    addBox(voxels, -2, 5, -2, 4, 1, 4, c.woodShadow);
    // Acorn body (golden wood)
    addBox(voxels, -1.5, 6, -1.5, 3, 2.5, 3, PALETTE.DIRT_LIGHT);
    // Little mail slot (mini-voxels!)
    addMiniBox(voxels, -1.0, 7.0, 1.5, 2, 1, 1, PALETTE.STONE_DARK);
    assets.push({
      id: `ext_mailbox_${finish}`,
      name: `Acorn Mailbox`,
      category: 'prop',
      description: 'A charming rustic postbox shaped like a giant hollow acorn standing on a mossy wood pole.',
      voxels,
      dimensions: { x: 4, y: 9, z: 4 }
    });
  })();

  // 2. Bentwood Bench
  (() => {
    const voxels: VoxelData[] = [];
    // Seat plank
    addBox(voxels, -4, 1.5, -1.5, 8, 0.6, 3, c.woodPrimary);
    // Curved bentwood legs
    addBox(voxels, -3.5, 0, -1.2, 0.8, 2, 0.8, c.woodShadow);
    addBox(voxels, 2.7, 0, -1.2, 0.8, 2, 0.8, c.woodShadow);
    addBox(voxels, -3.5, 0, 0.4, 0.8, 2, 0.8, c.woodShadow);
    addBox(voxels, 2.7, 0, 0.4, 0.8, 2, 0.8, c.woodShadow);
    // Interlocking twig stretchers (decorative backrest curves)
    addBox(voxels, -3.8, 2.1, -1.5, 7.6, 3, 0.6, c.woodShadow);
    addBox(voxels, -3.5, 4.5, -1.5, 7, 0.6, 0.6, c.woodHighlight as any);
    assets.push({
      id: `ext_bentwood_bench_${finish}`,
      name: `Bentwood Bench`,
      category: 'prop',
      description: 'An organic garden bench handcrafted from gracefully bent and woven tree branches.',
      voxels,
      dimensions: { x: 8, y: 5.5, z: 3 }
    });
  })();

  // 3. Bird Bath
  (() => {
    const voxels: VoxelData[] = [];
    // Pedestal stem
    addBox(voxels, -1, 0, -1, 2, 4, 2, c.stone);
    addBox(voxels, -1.5, 0, -1.5, 3, 0.8, 3, c.stone); // base base
    // Shallow stone dish bowl
    for (let x = -3; x <= 3; x++) {
      for (let z = -3; z <= 3; z++) {
        const dist = Math.hypot(x, z);
        if (dist <= 3.2 && dist >= 2.0) {
          addBox(voxels, x, 4, z, 1, 1, 1, c.stone);
        } else if (dist < 2.0) {
          addBox(voxels, x, 4, z, 1, 0.5, 1, c.stone); // bottom
          addBox(voxels, x, 4.5, z, 1, 0.5, 1, PALETTE.WATER_CYAN_DEEP, 'water'); // water disc
        }
      }
    }
    // A tiny bluebird perched on edge (mini-voxels!)
    addMiniBox(voxels, -4.0, 5.0, 0, 1.5, 1.5, 2, PALETTE.FLOWER_BLUE); // bird body
    addMiniBox(voxels, -4.0, 5.25, 1.0, 1, 1, 1, PALETTE.FLOWER_YELLOW); // yellow beak
    assets.push({
      id: `ext_bird_bath_${finish}`,
      name: `Stone Bird Bath`,
      category: 'prop',
      description: 'A hand-carved stone water basin containing shallow water and a tiny bluebird.',
      voxels,
      dimensions: { x: 7, y: 6, z: 7 }
    });
  })();

  // 4. Boot Scraper
  (() => {
    const voxels: VoxelData[] = [];
    // Small cobblestone flagstone base
    addBox(voxels, -2, 0, -1.5, 4, 0.5, 3, c.stone);
    // Iron scraping framework (mini-voxels!)
    addMiniBox(voxels, -2.5, 0.5, -1, 1, 3.5, 2, c.metal); // left iron support post
    addMiniBox(voxels, 1.5, 0.5, -1, 1, 3.5, 2, c.metal); // right post
    addMiniBox(voxels, -2.0, 2.0, -0.5, 4, 1, 1, c.metal); // horizontal scraper blade
    assets.push({
      id: `ext_boot_scraper_${finish}`,
      name: `Mud Boot Scraper`,
      category: 'prop',
      description: 'A traditional cast-iron scraper blade mounted on a heavy cobblestone step block.',
      voxels,
      dimensions: { x: 4, y: 2.5, z: 3 }
    });
  })();

  // 5. Clothes Line
  (() => {
    const voxels: VoxelData[] = [];
    // Post A (Left)
    addBox(voxels, -6, 0, -0.5, 1, 7, 1, c.woodShadow);
    addBox(voxels, -7, 6, -0.5, 3, 0.8, 1, c.woodPrimary); // Left T-bar Cross
    // Post B (Right)
    addBox(voxels, 5, 0, -0.5, 1, 7, 1, c.woodShadow);
    addBox(voxels, 4, 6, -0.5, 3, 0.8, 1, c.woodPrimary); // Right T-bar Cross
    // Clothespin wire running across
    for (let x = -5.5; x <= 4.5; x += 0.5) {
      voxels.push({ x, y: 6.2, z: 0, color: 0xcccccc, scale: 0.1 });
    }
    // A pair of hanging striped laundry shirts (mini-voxels!)
    // Left shirt (Red/white stripes)
    addMiniBox(voxels, -4.0, 4.0, 0, 4, 5, 1, PALETTE.FLOWER_RED);
    addMiniBox(voxels, -4.0, 6.0, 0, 4, 1, 1, PALETTE.FLOWER_WHITE);
    addMiniBox(voxels, -5.0, 5.0, 0, 2, 2, 1, PALETTE.FLOWER_RED); // sleeves
    addMiniBox(voxels, -2.0, 5.0, 0, 2, 2, 1, PALETTE.FLOWER_RED);
    // Right shirt (Blue)
    addMiniBox(voxels, 0.5, 4.0, 0, 4, 5, 1, PALETTE.FLOWER_BLUE);
    addMiniBox(voxels, -0.5, 5.0, 0, 2, 2, 1, PALETTE.FLOWER_BLUE);
    addMiniBox(voxels, 2.5, 5.0, 0, 2, 2, 1, PALETTE.FLOWER_BLUE);
    assets.push({
      id: `ext_clothes_line_${finish}`,
      name: `Clothes Line`,
      category: 'prop',
      description: 'A garden clothesline strung between timber T-posts holding colorful drying laundry.',
      voxels,
      dimensions: { x: 13, y: 7.5, z: 2 }
    });
  })();

  // 6. Copper Lantern
  (() => {
    const voxels: VoxelData[] = [];
    // Small hanging wall hook bracket
    addBox(voxels, 0, 4, -1, 0.6, 2, 2, c.woodShadow);
    addBox(voxels, 0, 5, 1, 0.6, 0.6, 2, c.metal as any);
    // Slender copper suspension chain
    voxels.push({ x: 0, y: 4.2, z: 2, color: c.metal, scale: 0.4 });
    // Glowing lantern body (mini-voxels!)
    addMiniBox(voxels, -1.5, 2.0, 2.5, 3, 1, 3, c.metal); // base
    addMiniBox(voxels, -1.0, 2.5, 3.0, 2, 3, 2, PALETTE.LAMP_GOLD, 'emissive'); // glass core
    addMiniBox(voxels, -1.5, 4.0, 2.5, 3, 1, 3, c.metal); // cap
    assets.push({
      id: `ext_copper_lantern_${finish}`,
      name: `Copper Lantern`,
      category: 'prop',
      description: 'A finely-crafted hanging copper lantern with an emissive golden glass core.',
      voxels,
      dimensions: { x: 3, y: 6.5, z: 4.5 }
    });
  })();

  // 7. Creek Stones
  (() => {
    const voxels: VoxelData[] = [];
    // Multi-scale smooth pebbles grouped (mini-voxels!)
    addMiniBox(voxels, -3.0, 0, -2.0, 3, 1.5, 3, c.stone); // stone 1
    addMiniBox(voxels, -2.5, 0, -1.5, 2.5, 1.2, 2.5, PALETTE.STONE_LIGHT);
    addMiniBox(voxels, 1.0, 0, -1.0, 4, 2.0, 3.5, PALETTE.STONE_DARK); // stone 2
    addMiniBox(voxels, -1.0, 0, 1.5, 3, 1.1, 2, c.stone); // stone 3
    assets.push({
      id: `ext_creek_stones_${finish}`,
      name: `Creek Stones`,
      category: 'prop',
      description: 'A scenic collection of rounded, smooth weathered slate creek pebbles.',
      voxels,
      dimensions: { x: 5, y: 1.5, z: 4 }
    });
  })();

  // 8. Garden Arch
  (() => {
    const voxels: VoxelData[] = [];
    // Double pillars
    for (let y = 0; y <= 9; y++) {
      voxels.push({ x: -4, y, z: -1, color: c.stone, scale: 1 });
      voxels.push({ x: 3, y, z: -1, color: c.stone, scale: 1 });
      voxels.push({ x: -4, y, z: 1, color: c.stone, scale: 1 });
      voxels.push({ x: 3, y, z: 1, color: c.stone, scale: 1 });
    }
    // High arched crossbars
    addBox(voxels, -4, 10, -1, 8, 1, 0.8, c.woodPrimary);
    addBox(voxels, -4, 10, 0.2, 8, 1, 0.8, c.woodPrimary);
    // Beautiful crawling leafy vines with colorful blooms (foliage)
    addBox(voxels, -4.2, 3, -1.2, 1, 7, 2.4, PALETTE.GRASS_LUSH, 'foliage');
    addBox(voxels, 3.2, 4, -1.2, 1, 6, 2.4, PALETTE.GRASS_LUSH, 'foliage');
    addBox(voxels, -3, 10, -1.2, 6, 1.2, 2.4, PALETTE.GRASS_BRIGHT, 'foliage');
    // Rose blossoms on vine
    voxels.push({ x: -4.3, y: 5, z: 0.2, color: PALETTE.FLOWER_RED });
    voxels.push({ x: 3.3, y: 7, z: -0.2, color: PALETTE.FLOWER_YELLOW });
    assets.push({
      id: `ext_garden_arch_${finish}`,
      name: `Garden Trellis Arch`,
      category: 'prop',
      description: 'A picturesque rustic stone arbor arch overrun with leafy garden ivy and climbing roses.',
      voxels,
      dimensions: { x: 9, y: 12, z: 4 }
    });
  })();

  // 9. Hanging Bell
  (() => {
    const voxels: VoxelData[] = [];
    // A-frame timber stand
    addBox(voxels, -1.5, 0, -1, 0.8, 6, 0.8, c.woodShadow);
    addBox(voxels, 1.1, 0, -1, 0.8, 6, 0.8, c.woodShadow);
    addBox(voxels, -1.5, 5.8, -1, 3.4, 0.8, 0.8, c.woodPrimary);
    // Hanging bronze bell (mini-voxels!)
    addMiniBox(voxels, -1.0, 4.5, -1.5, 2, 3, 2, c.metal); // link chain
    addMiniBox(voxels, -2.0, 2.5, -2.5, 4, 4.0, 4, PALETTE.THATCH_YELLOW); // copper bell body
    addMiniBox(voxels, -0.5, 1.5, -1.0, 1, 2.0, 1, c.metal); // clapper ringer handle
    assets.push({
      id: `ext_hanging_bell_${finish}`,
      name: `Hanging Bell`,
      category: 'prop',
      description: 'A heavy copper cottage calling bell hanging on a structural A-frame timber gantry.',
      voxels,
      dimensions: { x: 4, y: 7, z: 2 }
    });
  })();

  // 10. Herb Cart
  (() => {
    const voxels: VoxelData[] = [];
    // Wagon frame bed
    addBox(voxels, -3, 1.2, -2, 6, 0.6, 4, c.woodPrimary);
    // Side slats panels
    addBox(voxels, -3, 1.8, -2, 6, 1.5, 0.6, c.woodShadow);
    addBox(voxels, -3, 1.8, 1.4, 6, 1.5, 0.6, c.woodShadow);
    addBox(voxels, -3, 1.8, -1.4, 0.6, 1.5, 2.8, c.woodShadow);
    // Two large wooden spoked cart wheels on axles (height 3)
    for (let x = -2; x <= 2; x += 3.5) {
      for (let y = 0; y <= 3; y++) {
        voxels.push({ x, y, z: -2.3, color: c.woodShadow, scale: 0.9 });
        voxels.push({ x, y, z: 2.3, color: c.woodShadow, scale: 0.9 });
      }
    }
    // Handle posts
    addBox(voxels, 3, 1.5, -1.5, 3, 0.6, 0.6, c.woodPrimary);
    addBox(voxels, 3, 1.5, 0.9, 3, 0.6, 0.6, c.woodPrimary);
    // Clay herb pots stacked inside cart (mini-voxels!)
    addMiniBox(voxels, -4.0, 1.8, -2.0, 3, 3, 3, PALETTE.ROOF_RED_CLAY); // pot 1
    addMiniBox(voxels, -4.5, 3.3, -2.5, 4, 3, 4, PALETTE.GRASS_BRIGHT, 'foliage'); // parsley
    addMiniBox(voxels, 1.0, 1.8, 0, 3, 3, 3, PALETTE.ROOF_RED_CLAY); // pot 2
    addMiniBox(voxels, 0.5, 3.3, -0.5, 4, 3, 4, PALETTE.MOSS, 'foliage'); // mint
    assets.push({
      id: `ext_herb_cart_${finish}`,
      name: `Herb Cart`,
      category: 'prop',
      description: 'A charming, two-wheeled rolling garden barrow stacked with fragrant potted herbs.',
      voxels,
      dimensions: { x: 7, y: 5.5, z: 5.5 }
    });
  })();

  // 11. Little Footbridge
  (() => {
    const voxels: VoxelData[] = [];
    // Arched support bridge deck
    for (let x = -5; x <= 5; x++) {
      const dist = Math.abs(x);
      const y = Math.max(0, 1.2 - (dist * dist) * 0.04);
      addBox(voxels, x, y, -2, 1, 0.6, 4, c.woodPrimary);
    }
    // High round support handrails on sides
    for (let x = -5; x <= 5; x += 2.5) {
      const dist = Math.abs(x);
      const y = Math.max(0, 1.2 - (dist * dist) * 0.04);
      addBox(voxels, x, y + 0.6, -2, 0.8, 2, 0.8, c.woodShadow);
      addBox(voxels, x, y + 0.6, 1.2, 0.8, 2, 0.8, c.woodShadow);
    }
    assets.push({
      id: `ext_footbridge_${finish}`,
      name: `Timber Footbridge`,
      category: 'prop',
      description: 'A small wooden arched garden footbridge designed to cross shallow shire creeks.',
      voxels,
      dimensions: { x: 11, y: 4, z: 4 }
    });
  })();

  // 12. Log Pile
  (() => {
    const voxels: VoxelData[] = [];
    // Stacking logs in a triangular wedge pyramid
    // Row 1 (Bottom 3 logs)
    addBox(voxels, -3, 0, -2, 2, 1.5, 4, c.woodShadow);
    addBox(voxels, -1, 0, -2, 2, 1.5, 4, c.woodShadow);
    addBox(voxels, 1, 0, -2, 2, 1.5, 4, c.woodShadow);
    // End details
    addBox(voxels, -3, 0, -2, 0.4, 1.5, 4, PALETTE.WOOD_LOG_END);
    addBox(voxels, -1, 0, -2, 0.4, 1.5, 4, PALETTE.WOOD_LOG_END);
    addBox(voxels, 1, 0, -2, 0.4, 1.5, 4, PALETTE.WOOD_LOG_END);
    // Row 2 (Middle 2 logs)
    addBox(voxels, -2, 1.5, -2, 2, 1.5, 4, c.woodShadow);
    addBox(voxels, 0, 1.5, -2, 2, 1.5, 4, c.woodShadow);
    addBox(voxels, -2, 1.5, -2, 0.4, 1.5, 4, PALETTE.WOOD_LOG_END);
    addBox(voxels, 0, 1.5, -2, 0.4, 1.5, 4, PALETTE.WOOD_LOG_END);
    // Row 3 (Top 1 log)
    addBox(voxels, -1, 3.0, -2, 2, 1.5, 4, c.woodShadow);
    addBox(voxels, -1, 3.0, -2, 0.4, 1.5, 4, PALETTE.WOOD_LOG_END);
    assets.push({
      id: `ext_log_pile_${finish}`,
      name: `Firewood Log Pile`,
      category: 'prop',
      description: 'A stacked woodpile of split fireplace hearth logs showing detailed end rings.',
      voxels,
      dimensions: { x: 7, y: 5, z: 4.5 }
    });
  })();

  // 13. Mossy Barrel
  (() => {
    const voxels: VoxelData[] = [];
    // Cylindrical barrel body
    for (let y = 0; y <= 4; y++) {
      const isFatMid = y === 2 || y === 3;
      const r = isFatMid ? 2.5 : 2.0;
      for (let x = -3; x <= 3; x++) {
        for (let z = -3; z <= 3; z++) {
          const dist = Math.hypot(x, z);
          if (dist <= r && dist >= r - 1.0) {
            // Steel reinforcing hoop straps on top/bottom
            const isStrap = y === 1 || y === 4;
            const color = isStrap ? c.metal : c.woodPrimary;
            voxels.push({ x, y, z, color, scale: 0.95 });
          }
        }
      }
    }
    // Moss patches growing down (foliage)
    addBox(voxels, -2, 2.5, -2, 2, 1.5, 2.5, PALETTE.MOSS, 'foliage');
    assets.push({
      id: `ext_mossy_barrel_${finish}`,
      name: `Mossy Storage Barrel`,
      category: 'prop',
      description: 'A watertight oak storage cask bound in iron ribs and partially blanketed in green velvet moss.',
      voxels,
      dimensions: { x: 5, y: 5, z: 5 }
    });
  })();

  // 14. Painted Birdhouse
  (() => {
    const voxels: VoxelData[] = [];
    // Ground pole
    for (let y = 0; y <= 5; y++) {
      voxels.push({ x: 0, y, z: 0, color: c.woodShadow, scale: 0.6 });
    }
    // Small birdhouse body (colorful painted red)
    addBox(voxels, -1.5, 5, -1.5, 3, 3, 3, PALETTE.FLOWER_RED);
    // Yellow pitch gable roof shingle cap
    for (let x = -2; x <= 2; x++) {
      for (let z = -2; z <= 2; z++) {
        const distZ = Math.abs(z);
        const roofY = 8.5 - distZ * 0.7;
        voxels.push({ x, y: roofY, z, color: PALETTE.FLOWER_YELLOW, scale: 0.8 });
      }
    }
    // Entrance hole (mini-voxels!)
    addMiniBox(voxels, -0.5, 6.5, 1.5, 1, 1, 1, PALETTE.STONE_DARK); // door hole
    addMiniBox(voxels, -0.5, 5.5, 1.5, 1, 1, 2, PALETTE.THATCH_YELLOW); // perch peg
    assets.push({
      id: `ext_birdhouse_${finish}`,
      name: `Painted Birdhouse`,
      category: 'prop',
      description: 'A cozy gabled bird box painted cherry red on a tall garden spindle pole.',
      voxels,
      dimensions: { x: 4, y: 10, z: 4 }
    });
  })();

  // 15. Picnic Blanket
  (() => {
    const voxels: VoxelData[] = [];
    // Gingham checkered patterns red/white (flat 0.1 y-scale)
    for (let x = -5; x <= 5; x++) {
      for (let z = -4; z <= 4; z++) {
        const isRed = (x + z) % 2 === 0;
        const color = isRed ? PALETTE.FLOWER_RED : 0xffffff;
        voxels.push({ x, y: 0.05, z, color, scale: 1, scaleY: 0.1 });
      }
    }
    // Mini picnic basket on the blanket (mini-voxels!)
    addMiniBox(voxels, -2.0, 1.0, -1.0, 4, 3, 3, PALETTE.WOOD_OAK_LIGHT); // basket
    addMiniBox(voxels, -2.0, 4.0, 0, 1, 2, 1, PALETTE.WOOD_OAK_DARK); // arched handle
    addMiniBox(voxels, 2.0, 1.0, 1.0, 1.5, 1.5, 1.5, PALETTE.FLOWER_WHITE); // cup
    assets.push({
      id: `ext_picnic_blanket_${finish}`,
      name: `Gingham Picnic Blanket`,
      category: 'prop',
      description: 'A classic red-and-white checkered lawn spread holding a miniature wicker picnic basket.',
      voxels,
      dimensions: { x: 11, y: 1, z: 9 }
    });
  })();

  // 16. Porch Chair
  (() => {
    const voxels: VoxelData[] = [];
    // Legs
    addBox(voxels, -1.5, 0, -1.5, 0.8, 2, 0.8, c.woodShadow);
    addBox(voxels, 1.0, 0, -1.5, 0.8, 2, 0.8, c.woodShadow);
    addBox(voxels, -1.5, 0, 1.0, 0.8, 2, 0.8, c.woodShadow);
    addBox(voxels, 1.0, 0, 1.0, 0.8, 2, 0.8, c.woodShadow);
    // Slatted seat pad
    addBox(voxels, -2, 2, -2, 4.2, 0.5, 4.2, c.woodPrimary);
    // Arching high spindle backrest
    addBox(voxels, -2, 2.5, -2, 4.2, 4.5, 0.6, c.woodShadow);
    addBox(voxels, -2, 6.5, -2, 4.2, 0.6, 0.6, c.woodHighlight as any);
    assets.push({
      id: `ext_porch_chair_${finish}`,
      name: `Cottage Porch Chair`,
      category: 'prop',
      description: 'A high-back outdoor timber chair with robust support slats and weathered edges.',
      voxels,
      dimensions: { x: 5, y: 7.5, z: 5 }
    });
  })();

  // 17. Rain Chain
  (() => {
    const voxels: VoxelData[] = [];
    // Top bracket bar
    addBox(voxels, -1, 11, -0.5, 2, 0.6, 1, c.woodShadow);
    // Vertical copper hanging cups (mini-voxels!)
    for (let cy = 0.5; cy <= 10.5; cy += 2.0) {
      addMiniBox(voxels, -1.0, cy, -1.0, 2, 1.5, 2, c.metal); // copper cup
      // sparkling water drops (emissive/water)
      addMiniBox(voxels, -0.5, cy + 1.0, -0.5, 1, 1, 1, PALETTE.WATER_FOAM, 'water');
    }
    assets.push({
      id: `ext_rain_chain_${finish}`,
      name: `Copper Rain Chain`,
      category: 'prop',
      description: 'A hand-hammered vertical copper rain chain draining roof runoff with delicate bells.',
      voxels,
      dimensions: { x: 2, y: 12, z: 2 }
    });
  })();

  // 18. Round Table
  (() => {
    const voxels: VoxelData[] = [];
    // Cross timber legs
    addBox(voxels, -2, 0, -2, 4, 3.5, 0.8, c.woodShadow);
    addBox(voxels, -2, 0, 1.2, 4, 3.5, 0.8, c.woodShadow);
    // Circular table top
    for (let x = -3.5; x <= 3.5; x++) {
      for (let z = -3.5; z <= 3.5; z++) {
        if (Math.hypot(x, z) <= 3.6) {
          voxels.push({ x, y: 3.5, z, color: c.woodPrimary });
        }
      }
    }
    assets.push({
      id: `ext_round_table_${finish}`,
      name: `Garden Round Table`,
      category: 'prop',
      description: 'A stout, weathered wooden circular garden table with robust support trestles.',
      voxels,
      dimensions: { x: 8, y: 4.5, z: 8 }
    });
  })();

  // 19. Scarecrow
  (() => {
    const voxels: VoxelData[] = [];
    // Central structural wooden cross pole
    addBox(voxels, -0.5, 0, -0.5, 1, 9, 1, c.woodShadow);
    addBox(voxels, -3, 6.5, -0.5, 6.5, 1, 1, c.woodShadow); // shoulder beam
    // Cozy tattered blue shirt clothes hanging on shoulder beam
    addBox(voxels, -2.5, 4.5, -1, 5, 2.5, 2, PALETTE.FLOWER_BLUE);
    // Pumpkin head with carved triangular eyes (emissive orange glow!)
    addBox(voxels, -1.5, 7, -1.5, 3, 3, 3, PALETTE.PUMPKIN_ORANGE);
    // Carved face (mini-voxels!)
    addMiniBox(voxels, -1.0, 8.0, 1.5, 1, 1, 1, PALETTE.STONE_DARK); // eye
    addMiniBox(voxels, 0.5, 8.0, 1.5, 1, 1, 1, PALETTE.STONE_DARK); // eye
    addMiniBox(voxels, -0.5, 7.25, 1.5, 2, 1, 1, PALETTE.STONE_DARK); // mouth
    // Floppy wide witch/scarecrow hat top
    addBox(voxels, -2.5, 9.8, -2.5, 5, 0.5, 5, c.woodShadow);
    addBox(voxels, -1.5, 10.3, -1.5, 3, 1.5, 3, c.woodShadow);
    assets.push({
      id: `ext_scarecrow_${finish}`,
      name: `Pumpkin Scarecrow`,
      category: 'prop',
      description: 'A straw-filled farm scarecrow on a post, wearing a tattered shirt and glowing pumpkin head.',
      voxels,
      dimensions: { x: 7, y: 12, z: 5 }
    });
  })();

  // 20. Stone Basin
  (() => {
    const voxels: VoxelData[] = [];
    // Giant natural hollowed granite boulder basin filled with spring water
    for (let x = -3; x <= 3; x++) {
      for (let y = 0; y <= 3; y++) {
        for (let z = -3; z <= 3; z++) {
          const dist = Math.hypot(x, z);
          const isLid = y === 3;
          if (dist <= 3.2 && dist >= 2.0) {
            voxels.push({ x, y, z, color: c.stone });
          } else if (dist < 2.0) {
            if (y === 0) {
              voxels.push({ x, y, z, color: c.stone });
            } else if (y <= 2) {
              voxels.push({ x, y, z: isLid ? z : z, color: PALETTE.WATER_CYAN, materialType: 'water' }); // deep water inside
            }
          }
        }
      }
    }
    // Moss patches growing on rock
    addBox(voxels, -2, 1, 2.2, 1.5, 1.5, 1, PALETTE.MOSS, 'foliage');
    assets.push({
      id: `ext_stone_basin_${finish}`,
      name: `Stone Water Basin`,
      category: 'prop',
      description: 'A rustic springwater collection basin carved directly out of a solid mossy river boulder.',
      voxels,
      dimensions: { x: 7, y: 4, z: 7 }
    });
  })();

  // 21. Story Post
  (() => {
    const voxels: VoxelData[] = [];
    // Wood pillar
    addBox(voxels, -1, 0, -1, 2, 9, 2, c.woodShadow);
    // Mossy base
    addBox(voxels, -1.5, 0, -1.5, 3, 0.6, 3, PALETTE.MOSS);
    // Arrow Sign 1: pointing left (The Shire)
    addBox(voxels, -4.5, 6.5, -0.6, 4.5, 1.2, 0.4, c.woodPrimary);
    // Text carving lines (mini-voxels!)
    addMiniBox(voxels, -3.5, 6.5, -0.2, 4, 1, 1, PALETTE.FLOWER_WHITE);
    // Arrow Sign 2: pointing right (Old Mill)
    addBox(voxels, 1.0, 4.5, -0.2, 4.5, 1.2, 0.4, c.woodPrimary);
    addMiniBox(voxels, 1.5, 4.5, 0.2, 4, 1, 1, PALETTE.FLOWER_WHITE);
    assets.push({
      id: `ext_story_post_${finish}`,
      name: `Shire Directional Signpost`,
      category: 'prop',
      description: 'A weathered oak crossroads post holding hand-carved directional arrow boards.',
      voxels,
      dimensions: { x: 9, y: 10, z: 3 }
    });
  })();

  // 22. Sun Dial
  (() => {
    const voxels: VoxelData[] = [];
    // Classical fluted column pedestal
    addBox(voxels, -1.5, 0, -1.5, 3, 0.8, 3, c.stone); // base ring
    addBox(voxels, -1, 0.8, -1, 2, 3.5, 2, c.stone); // column stem
    addBox(voxels, -1.5, 4.3, -1.5, 3, 0.8, 3, c.stone); // top cap plate
    // Dial gnomon indicator needle (mini-voxels!)
    addMiniBox(voxels, -1.0, 5.1, -1.0, 2, 1, 2, PALETTE.LAMP_GOLD); // flat round bronze disc
    addMiniBox(voxels, -0.5, 5.6, -0.5, 1, 1, 2, PALETTE.LAMP_GOLD); // triangular gnomon shadows pointer
    assets.push({
      id: `ext_sundial_${finish}`,
      name: `Bronze Sun Dial`,
      category: 'prop',
      description: 'An elegant sundial pointer mounted on a hand-chiselled stone pedestal column.',
      voxels,
      dimensions: { x: 4, y: 6, z: 4 }
    });
  })();

  // 23. Tea Cart
  (() => {
    const voxels: VoxelData[] = [];
    // Barrow frame tray
    addBox(voxels, -2, 1.2, -1.5, 4, 0.5, 3, c.woodPrimary);
    // Side handle posts
    addBox(voxels, 2, 1.5, -1.2, 2.5, 0.5, 0.5, c.woodShadow);
    addBox(voxels, 2, 1.5, 0.7, 2.5, 0.5, 0.5, c.woodShadow);
    // Two high front spoked wooden wheels (mini-voxels!)
    for (let y = 0; y <= 3; y++) {
      voxels.push({ x: -1.5, y, z: -1.8, color: c.woodShadow, scale: 0.8 });
      voxels.push({ x: -1.5, y, z: 1.8, color: c.woodShadow, scale: 0.8 });
    }
    assets.push({
      id: `ext_teacart_${finish}`,
      name: `Cottage Tea Cart`,
      category: 'prop',
      description: 'A two-wheeled rolling wood cart tray designed for taking tea out to the garden lawn.',
      voxels,
      dimensions: { x: 5, y: 3, z: 4.5 }
    });
  })();

  // 24. Tool Rack
  (() => {
    const voxels: VoxelData[] = [];
    // Hanging board backing
    addBox(voxels, -3, 3, -0.6, 6, 4, 0.6, c.woodPrimary);
    // Hanging Pitchfork (mini-voxels!)
    addMiniBox(voxels, -3.0, 2.0, 0.5, 1, 10, 1, PALETTE.THATCH_YELLOW); // wood handle
    addMiniBox(voxels, -3.5, 1.0, 0.5, 2, 2, 1, c.metal); // metal fork tines
    // Hanging Straw Broom (mini-voxels!)
    addMiniBox(voxels, 2.0, 4.0, 0.5, 1, 8, 1, PALETTE.WOOD_OAK_DARK); // broom stick
    addMiniBox(voxels, 1.5, 1.0, 0.5, 2, 3, 2, PALETTE.THATCH_YELLOW); // straw bristles
    assets.push({
      id: `ext_tool_rack_${finish}`,
      name: `Cottage Tool Rack`,
      category: 'prop',
      description: 'A wall-mounted gear bracket rack holding a detailed farm pitchfork and broom.',
      voxels,
      dimensions: { x: 6, y: 6, z: 2 }
    });
  })();

  // 25. Village Banner
  (() => {
    const voxels: VoxelData[] = [];
    // Tall wooden flagpole
    addBox(voxels, -0.5, 0, -0.5, 1, 14, 1, c.woodShadow);
    // Banner hanging cross bracket
    addBox(voxels, -3, 12, -0.5, 6, 1, 1, c.woodPrimary);
    // Fabric banner emblem (flowing cloth look with varying widths)
    for (let y = 3; y <= 11; y++) {
      addBox(voxels, -2, y, -0.1, 4, 1, 0.2, y % 2 === 0 ? PALETTE.FLOWER_RED : PALETTE.WHEAT_GOLD);
    }
    // Spear point topper
    voxels.push({ x: 0, y: 14.2, z: 0, color: PALETTE.LAMP_GOLD, scale: 0.8 });
    assets.push({
      id: `ext_banner_${finish}`,
      name: `Village Honor Banner`,
      category: 'prop',
      description: 'A tall wooden heraldry staff flying a long, split gold-and-crimson woven crest.',
      voxels,
      dimensions: { x: 6, y: 15, z: 2 }
    });
  })();

  // 26. Watering Can
  (() => {
    const voxels: VoxelData[] = [];
    // Watering can cylindrical body (mini-voxels!)
    addMiniBox(voxels, -2.5, 0, -2.5, 5, 5, 5, c.metal); // copper can body
    // Arched holding handle
    addMiniBox(voxels, -4.5, 2, -1.0, 2, 4, 2, c.metal);
    // Spout tube extending forward
    addMiniBox(voxels, 2.5, 1.5, -0.5, 4, 1.5, 1, c.metal); // tube
    addMiniBox(voxels, 6.0, 2.5, -1.0, 1.5, 1.5, 2, PALETTE.LAMP_GOLD); // sprinkle rose cap
    assets.push({
      id: `ext_watering_can_${finish}`,
      name: `Watering Can`,
      category: 'prop',
      description: 'A small hand-held copper garden watering can with a long tapered rose spout.',
      voxels,
      dimensions: { x: 5, y: 3, z: 3 }
    });
  })();

  // 27. Weather Vane
  (() => {
    const voxels: VoxelData[] = [];
    // Slender roof post mount
    for (let y = 0; y <= 6; y++) {
      voxels.push({ x: 0, y, z: 0, color: c.metal, scale: 0.4 });
    }
    // Directional cardinal letters N-S-E-W crossarms
    voxels.push({ x: -1.5, y: 4, z: 0, color: c.metal, scale: 0.4 });
    voxels.push({ x: 1.5, y: 4, z: 0, color: c.metal, scale: 0.4 });
    voxels.push({ x: 0, y: 4, z: -1.5, color: c.metal, scale: 0.4 });
    voxels.push({ x: 0, y: 4, z: 1.5, color: c.metal, scale: 0.4 });
    // Golden arrow and proud rooster topper outline (mini-voxels!)
    addMiniBox(voxels, -3.5, 7.0, 0, 7, 1, 1, c.metal); // horizontal pointer arrow
    addMiniBox(voxels, -1.5, 7.5, 0, 3, 3, 1, PALETTE.LAMP_GOLD, 'emissive'); // rooster cockerel cut-out
    addMiniBox(voxels, 1.0, 8.5, 0, 1.5, 1.5, 1, PALETTE.FLOWER_RED); // rooster tail/comb details
    assets.push({
      id: `ext_weathervane_${finish}`,
      name: `Rooster Weather Vane`,
      category: 'prop',
      description: 'A black iron compass peak pointer topped with a proud, spinning golden copper rooster.',
      voxels,
      dimensions: { x: 4, y: 8.5, z: 4 }
    });
  })();

  // 28. Wheelbarrow
  (() => {
    const voxels: VoxelData[] = [];
    // Cart bed hopper box
    addBox(voxels, -1, 1.2, -2, 4, 1.5, 4, c.woodPrimary);
    // Two long handles underneath extending backward
    addBox(voxels, -1.2, 0.8, -1.5, 6, 0.5, 0.5, c.woodShadow);
    addBox(voxels, -1.2, 0.8, 1.0, 6, 0.5, 0.5, c.woodShadow);
    // Support landing legs
    addBox(voxels, 3.5, 0, -1.5, 0.5, 0.8, 0.5, c.woodShadow);
    addBox(voxels, 3.5, 0, 1.0, 0.5, 0.8, 0.5, c.woodShadow);
    // High front wood-spoked wheel (mini-voxels!)
    for (let y = 0; y <= 3; y++) {
      voxels.push({ x: -2.3, y, z: 0, color: c.woodShadow, scale: 0.9 });
    }
    // Packed soil and pumpkins inside hopper
    addBox(voxels, -0.5, 2.0, -1.5, 3, 1.0, 3, PALETTE.DIRT_DARK);
    addBox(voxels, 0.5, 2.5, -0.5, 1.5, 1.5, 1.5, PALETTE.PUMPKIN_ORANGE);
    assets.push({
      id: `ext_wheelbarrow_${finish}`,
      name: `Farmer Wheelbarrow`,
      category: 'prop',
      description: 'A rustic, single-wheeled farm wheelbarrow containing fresh rich garden soil and a pumpkin.',
      voxels,
      dimensions: { x: 7, y: 4, z: 5 }
    });
  })();

  // 29. Wildflower Crate
  (() => {
    const voxels: VoxelData[] = [];
    // Slatted crate box (mini-voxels!)
    addMiniBox(voxels, -3.5, 0, -2.5, 7, 0.8, 5, c.woodShadow); // bottom
    addMiniBox(voxels, -3.5, 0.8, -2.5, 1, 3.5, 5, c.woodPrimary); // left side
    addMiniBox(voxels, 2.5, 0.8, -2.5, 1, 3.5, 5, c.woodPrimary); // right side
    addMiniBox(voxels, -2.5, 0.8, -2.5, 5, 3.5, 1, c.woodPrimary); // front
    addMiniBox(voxels, -2.5, 0.8, 1.5, 5, 3.5, 1, c.woodPrimary); // back
    // Rich soil base
    addMiniBox(voxels, -2.5, 0.8, -1.5, 5, 1.2, 3, PALETTE.DIRT_DARK);
    // Dense colorful wildflower blooms (foliage + emissives)
    addMiniBox(voxels, -3.0, 2.0, -2.0, 6, 2.5, 4, PALETTE.GRASS_BRIGHT, 'foliage');
    addMiniBox(voxels, -2.5, 4.5, -1.5, 2, 2, 2, PALETTE.FLOWER_RED); // red roses
    addMiniBox(voxels, 0.5, 4.5, -0.5, 2, 2, 2, PALETTE.FLOWER_BLUE); // bluebells
    assets.push({
      id: `ext_wildflower_crate_${finish}`,
      name: `Wildflower Wood Crate`,
      category: 'prop',
      description: 'A hand-slatted pine fruit crate bursting with blooming red roses and sky-blue cornflowers.',
      voxels,
      dimensions: { x: 4, y: 3.5, z: 3 }
    });
  })();

  // 30. Wooden Swing
  (() => {
    const voxels: VoxelData[] = [];
    // Two tall gallows pillars
    addBox(voxels, -4, 0, -1, 1, 11, 2, c.woodShadow);
    addBox(voxels, 3, 0, -1, 1, 11, 2, c.woodShadow);
    // Strong top crossbar log
    addBox(voxels, -4.5, 11, -1, 10, 1.2, 2, c.woodPrimary);
    // Swing hanging ropes (mini-voxels!)
    for (let ry = 2.0; ry <= 11.0; ry += 0.5) {
      voxels.push({ x: -2.0, y: ry, z: 0, color: PALETTE.THATCH_YELLOW, scale: 0.2 });
      voxels.push({ x: 2.0, y: ry, z: 0, color: PALETTE.THATCH_YELLOW, scale: 0.2 });
    }
    // Hanging weathered wood seating board plank
    addBox(voxels, -2.5, 1.8, -1, 5, 0.5, 2, c.woodHighlight);
    assets.push({
      id: `ext_wooden_swing_${finish}`,
      name: `Cottage Wooden Swing`,
      category: 'prop',
      description: 'A cozy timber garden swingset hung with heavy fiber hemp ropes and a smooth seat plank.',
      voxels,
      dimensions: { x: 10, y: 12.5, z: 3 }
    });
  })();

  return assets;
}
